import { SHOT_OUTCOME_LABELS } from '@golf/common';
import type { ShotOutcome } from '@golf/common';
import { Check } from 'lucide-react';
import { useAuth } from '../../lib/auth.tsx';
import { ShotIcon } from './ShotIcon.tsx';

/** Les cinq façons de rater qu'on peut nommer. `miss` n'en fait pas partie :
 *  c'est le raté qu'on n'a pas nommé, et il a son propre bouton. */
type Faute = 'hook' | 'slice' | 'fat' | 'top' | 'shank';

interface Props {
    onPick: (outcome: ShotOutcome) => void | Promise<void>;
    disabled?: boolean;
}

/* Le pavé de saisie d'une balle au practice.
 *
 * ── Il a servi au parcours, il n'y sert plus ────────────────────────────────
 *
 * Le tour en cours a longtemps posé la même question : quel contact ? Elle n'y
 * menait nulle part. Sur le parcours on joue une balle et on marche jusqu'à
 * elle ; ce qui décide de la suite est l'endroit où elle repose, pas la façon
 * dont on l'a frappée. Le club se choisit sur la zone, la pénalité se compte
 * sur la zone, et fairway en régulation, green en régulation et scrambling s'en
 * déduisent — aucun de ces trois ne sort d'un « slice ». Le parcours a donc son
 * pavé à lui, `rounds/PaveZone.tsx`.
 *
 * Ici la question reste la bonne, et c'est même la seule : on tape cinquante
 * balles avec le même fer pour voir quelle faute revient. Les huit réponses
 * SONT la séance, et elles prennent l'écran en conséquence.
 *
 * ── Un appui, un coup ───────────────────────────────────────────────────────
 *
 * Chaque bouton enregistre directement le coup qu'il nomme. Pas de seconde
 * étape, pas de tiroir à déplier, rien à confirmer.
 *
 * Une version intermédiaire enregistrait un raté nu, puis dépliait une boîte
 * pour demander pourquoi. Elle avait un défaut qui ne se voit qu'en jouant :
 * sur le parcours, l'écran propose DÉJÀ le sac pour le club suivant après un
 * coup, et deux boîtes s'ouvraient donc en même temps. Il fallait en fermer
 * une pour voir l'autre, à chaque balle.
 *
 * D'où cette forme : tout est là, tout le temps, et un seul appui suffit.
 *
 * ── Trois tailles, trois fréquences ─────────────────────────────────────────
 *
 * « Bon contact » prend toute la place qui reste : c'est ce qu'on touche le
 * plus, et il doit se trouver à la taille et à la position avant même d'être
 * lu.
 *
 * « Mauvais contact » vient juste après — c'est la réponse honnête quand on ne
 * sait pas nommer ce qui s'est passé, ou qu'on n'a pas le temps. Les deux se
 * nomment l'un par rapport à l'autre, et portent la même paire de couleurs
 * claires : laiton d'un côté, brique de l'autre, à clarté égale. La teinte
 * seule dit lequel est lequel — un rouge d'alarme aurait fait d'une balle
 * manquée un incident, alors que c'est la chose la plus ordinaire du golf.
 *
 * Les cinq fautes nommées sont en tuiles, sur le même papier chaud que les
 * clubs du sac : c'est le même geste — un choix plus fin, offert à qui le
 * veut, jamais réclamé.
 *
 * ── La position dit la direction ────────────────────────────────────────────
 *
 * Un slice part à droite : sa tuile est à droite. Un hook part à gauche : sa
 * tuile est à gauche. Le socket, qui fuse encore plus à droite, occupe
 * l'extrémité droite de la rangée suivante. Le joueur n'a pas à traduire un
 * mot en direction — il tape du côté où il a vu partir sa balle.
 *
 * Pour un GAUCHER tout s'inverse, trajectoires comme icônes. Sans cela, la
 * moitié des tuiles désigneraient le mauvais côté, ce qui est pire que pas de
 * repère du tout.
 *
 * Les fautes de contact — top, gratte — n'ont pas de sens latéral : elles
 * restent au centre et en bas, hors de la logique gauche/droite.
 *
 * L'ordre est fixe et ne dépend d'aucune statistique. La mémoire musculaire
 * vaut mieux qu'un ordre optimal : après trois séances, le pouce trouve
 * « slice » sans que l'œil quitte la balle.
 */
export function PaveCoup({ onPick, disabled = false }: Props) {
    const { user } = useAuth();
    const mirror = user?.dominantHand === 'left';

    // Le hook est à gauche pour un droitier, à droite pour un gaucher. Tuples
    // et non tableaux : sous noUncheckedIndexedAccess, déstructurer un Faute[]
    // donne des `| undefined` que le typage refuse ensuite.
    const [gauche, droite]: [Faute, Faute] = mirror ? ['slice', 'hook'] : ['hook', 'slice'];
    const contact: [Faute, Faute, Faute] = mirror
        ? ['shank', 'top', 'fat']
        : ['fat', 'top', 'shank'];

    const Tuile = ({ faute }: { faute: Faute }) => (
        <button
            type="button"
            disabled={disabled}
            onClick={() => void onPick(faute)}
            className="flex min-h-0 flex-col items-center justify-center gap-1 rounded-field
                       px-1 py-2 transition-transform active:scale-95 disabled:opacity-40"
            style={{ background: 'var(--tuile)', color: 'var(--tuile-encre)' }}
        >
            {/* L'illustration est bornée par `clamp` plutôt que fixée : la
                rangée s'étire avec l'écran, et un dessin figé à 36 px y
                flotterait au milieu d'une tuile de 120. */}
            <ShotIcon outcome={faute} mirror={mirror}
                className="h-[clamp(3rem,14vw,5rem)] w-[clamp(3rem,14vw,5rem)]" />
            <span className="text-[clamp(0.9rem,4vw,1.4rem)] font-bold leading-none">
                {SHOT_OUTCOME_LABELS[faute]}
            </span>
        </button>
    );

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
            {/* Le check reste en laiton et garde son ombre portée — trait à
                3,5 de graisse, où il tient 3:1 sur le vert d'action, la même
                parade que pour les icônes de la barre du haut. Le mot vient
                à côté et non en dessous : sur un petit téléphone le bouton
                tombe à 74 px, où deux étages ne tiendraient pas. */}
            <button
                type="button"
                disabled={disabled}
                onClick={() => void onPick('center')}
                /* `flex-1` : il prend tout ce qui reste — 198 px mesurés sur
                   un 430×932, 110 sur un 390×844, 74 sur un 360×740. Le
                   plancher de 4,5 rem est celui qui fait tenir la dernière
                   rangée de tuiles au-dessus du pli sur le plus petit des
                   trois, et il reste au-dessus des 56 px de cible tactile du
                   projet. */
                className="btn btn-primary min-h-[4.5rem] w-full flex-1 gap-3 shadow-md"
            >
                <Check
                    aria-hidden="true"
                    strokeWidth={3.5}
                    className="h-[clamp(2.5rem,12vw,4rem)] w-auto shrink-0 text-accent
                               drop-shadow-[0_1px_3px_rgba(0,0,0,0.35)]"
                />
                <span className="text-[clamp(1.25rem,6vw,2rem)] font-bold leading-none">
                    Bon contact
                </span>
            </button>

            {/* Ce bloc PARTAGE la hauteur libre avec le bouton vert au lieu de
                se contenter de sa taille naturelle : les huit réponses sont
                l'objet même de la séance, et une rangée de tuiles écrasée en
                bas d'écran dirait le contraire. */}
            <div className="flex min-h-0 flex-[2] flex-col gap-2">
                {/* « Bon contact » / « Mauvais contact » : les deux boutons se
                    nomment l'un par rapport à l'autre, et c'est ce qui rend le
                    choix immédiat. « Réussi » / « Raté » ne disaient pas qu'ils
                    s'opposaient — il fallait lire les deux pour le comprendre.

                    L'aplat est la brique du thème : même clarté que le laiton
                    du check au-dessus, donc du même niveau que lui. */}
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => void onPick('miss')}
                    className="flex min-h-[4.5rem] w-full shrink-0 items-center justify-center gap-3
                               rounded-field text-2xl font-bold shadow-sm transition-transform
                               active:scale-[0.98] disabled:opacity-40"
                    style={{
                        background: 'var(--brique)',
                        color: 'var(--brique-encre)',
                        border: '2px solid var(--brique-bord)',
                    }}
                >
                    <ShotIcon outcome="miss" className="h-12 w-12 shrink-0" />
                    Mauvais contact
                </button>

                {/* Sans cette ligne, cinq tuiles muettes sous un gros bouton se
                    lisent comme une étape à franchir. Elle dit que non : le
                    coup est enregistrable au-dessus comme en dessous, avec un
                    nom en plus. En encre laiton, la couleur de ce qui est
                    offert plutôt qu'exigé. */}
                <p className="shrink-0 px-1 text-center text-sm font-semibold"
                    style={{ color: 'var(--brass-ink)' }}>
                    ou dites ce qui s'est passé
                </p>

                <div className="grid min-h-0 flex-1 grid-cols-2 gap-2">
                    <Tuile faute={gauche} />
                    <Tuile faute={droite} />
                </div>
                <div className="grid min-h-0 flex-1 grid-cols-3 gap-2">
                    <Tuile faute={contact[0]} />
                    <Tuile faute={contact[1]} />
                    <Tuile faute={contact[2]} />
                </div>
            </div>
        </div>
    );
}
